// src/components/searchBars/SuggestionList.jsx
import { useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import StockLogo from '../StockLogo/StockLogoRender';
import CredibilityChip from '../Credibility/CredibilityChip';
import { resolveMediaUrl } from '../../utils/resolveMediaUrl';

const withAt = (value = '') => (value?.startsWith('@') ? value : `@${value}`);

const exchangeMap = {
  XNAS: "NASDAQ",
  XNYS: "NYSE",
  OTC: "OTC",
  XASE: "AMEX",
  XBER: "Frankfurt",
  XAMS: "Euronext Amsterdam",
};

const SuggestionItem = memo(
  ({ item, type, isOverlay, onSuggestionClick, onSuggestionHover, onSelect }) => {
    const symbol = (item.symbol || '').toUpperCase();
    const username = item.username || item.name || "";
    const email = item.email || "";
    const displayName = item.name || item.full_name || "";
    const exchange = exchangeMap[item.primary_exchange] || item.primary_exchange || "";
    const description = item.description || "";

    const valueForHandlers = type === "stock" ? symbol : withAt(username);

    const trigger = () => {
      const normalized = {
        ...item,
        symbol: item.symbol || item.ticker || null,
        username: item.username || item.name || null
      };
      //console.log('[SuggestionItem] click', normalized);
      onSuggestionClick(normalized);

      if (!isOverlay) {
        onSelect?.(item);
      }
    };


    return (
      <li
        className="p-3 hover:bg-gray-100 dark:hover:bg-white/10 focus:bg-gray-200 dark:focus:bg-white/20 cursor-pointer
                   transition-colors duration-200 outline-none text-gray-900 dark:text-white
                   border-b border-gray-200 dark:border-gray-800/50 last:border-b-0"
        role="option"
        tabIndex={0}
        aria-selected="false"
        onClick={trigger}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            trigger();
          }
        }}
        onMouseEnter={() => onSuggestionHover?.(valueForHandlers)}
      >
        <div className="flex items-center gap-3">
          {type === "stock" ? (
            <StockLogo symbol={symbol} name={displayName} size="w-8 h-8" />
          ) : (
            // Matches PublicAvatarDisplay.jsx's fallback exactly (the same
            // default alien avatar shown on profile pages for the ~all
            // users who haven't uploaded a real picture) -- the search
            // dropdown needs to look like the same person's avatar the
            // user just saw on that person's profile, not a different
            // fallback treatment.
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 overflow-hidden ring-2 ring-gray-300 dark:ring-gray-700/50 flex-shrink-0">
              <img
                src={resolveMediaUrl(item.display_picture_url) || "/avatars/aliens/alien_cool.svg"}
                alt={username}
                className="w-full h-full object-cover"
                onError={(e) => {
                  if (e.currentTarget.src.indexOf("alien_cool.svg") === -1) {
                    e.currentTarget.src = "/avatars/aliens/alien_cool.svg";
                  }
                }}
              />
            </div>
          )}

          <div className="flex-1 min-w-0">
            {type === "stock" ? (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-lg">{symbol}</span>
                  {exchange && (
                    <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700/60 rounded-full whitespace-nowrap">
                      {exchange}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{displayName}</p>
                {description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">{description}</p>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-indigo-600 dark:text-indigo-400">@</span>
                  <span className="font-semibold truncate">{username}</span>
                  <CredibilityChip cred={item.credibility} size="xs" showAccuracy={false} />
                </div>
                {displayName && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{displayName}</p>
                )}
                {email && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{email}</p>}
              </>
            )}
          </div>
        </div>
      </li>
    );
  }
);
SuggestionItem.displayName = "SuggestionItem";

const SectionHeader = memo(({ title }) => (
  <li className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase
                 tracking-wide bg-gray-100 dark:bg-gray-900/60 backdrop-blur-md border-b border-gray-200 dark:border-gray-700/40
                 sticky top-0 z-10">
    {title}
  </li>
));
SectionHeader.displayName = "SectionHeader";

const SuggestionList = ({
  suggestions,
  onSuggestionClick,
  onSuggestionHover,
  onSelect = () => {},
  isOverlay = false
}) => {
  const { t } = useTranslation();

  const handleKeyNavigation = useCallback((e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const items = Array.from(document.querySelectorAll("[role='option']"));
      if (!items.length) return;

      const currentIndex = items.findIndex((el) => el === document.activeElement);
      const nextIndex =
        e.key === "ArrowDown"
          ? (currentIndex + 1) % items.length
          : (currentIndex - 1 + items.length) % items.length;

      items[nextIndex]?.focus();
    }
  }, []);

  if (!suggestions?.length) return null;

  const stocks = suggestions.filter((item) => item.symbol || item.ticker);
  const users = suggestions.filter((item) => !(item.symbol || item.ticker));


  return (
    <ul
      className={`
        ${isOverlay
          ? "relative w-full mt-2 z-[100003]"
          : "absolute top-full left-0 w-full z-[100000] mt-1"}

        bg-white dark:bg-gray-900/95 backdrop-blur-xl
        shadow-2xl border border-gray-200 dark:border-gray-700

        rounded-lg
        ${isOverlay ? "" : "rounded-t-none"}

        max-h-[40vh] sm:max-h-80
        overflow-y-auto
        overscroll-contain
        touch-pan-y
      `}
      role="listbox"
      onKeyDown={handleKeyNavigation}
      aria-label={t('nav.searchSuggestionsLabel')}
    >
      {stocks.length > 0 && (
        <>
          <SectionHeader title={t('sidebar.stocks')} />
          {stocks.map((stock, i) => (
            <SuggestionItem
              key={`stock-${stock.symbol}-${i}`}
              item={stock}
              type="stock"
              isOverlay={isOverlay}
              onSuggestionClick={onSuggestionClick}
              onSuggestionHover={onSuggestionHover}
              onSelect={onSelect}
            />
          ))}
        </>
      )}

      {users.length > 0 && (
        <>
          <SectionHeader title={t('nav.users')} />
          {users.map((user, i) => (
            <SuggestionItem
              key={`user-${user.id ?? user.username ?? i}`}
              item={user}
              type="user"
              isOverlay={isOverlay}
              onSuggestionClick={onSuggestionClick}
              onSuggestionHover={onSuggestionHover}
              onSelect={onSelect}
            />
          ))}
        </>
      )}
    </ul>
  );
};

SuggestionList.defaultProps = { suggestions: [] };

export default memo(SuggestionList);